import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import useSEO from '@/hooks/useSEO';
import { Button } from '@/components/ui/button';
import { ShieldCheck, CheckCircle2, FileCheck2, ScanFace, Award, ArrowRight, Sparkles } from 'lucide-react';

/**
 * Sales-pitch landing page for the "Verified Agents" trust pillar.
 * Linked from the homepage trust box and footer.
 */
export default function WhyVerifiedAgentsPage() {
  const { language } = useLanguage();
  const es = language === 'es';

  const t = (en, esT) => (es ? esT : en);

  useSEO({
    title: t('Verified Agents — Win More Clients on WakaCasa', 'Agentes Verificados — Gana Más Clientes en WakaCasa'),
    description: t(
      'WakaCasa-verified agents pass ID, selfie and licence checks with AI + human review. +25 Trust Score, premium ranking, eligible for the diaspora flow.',
      'Los agentes verificados por WakaCasa pasan revisión de cédula, selfie y licencia con IA + revisión humana. +25 en Trust Score, posicionamiento prioritario y flujo de diáspora habilitado.',
    ),
    canonical: '/why/verified-agents',
    image: '/sales/agent-verification.jpeg',
  });

  const steps = [
    {
      icon: FileCheck2,
      title: t('1. Upload your documents', '1. Sube tus documentos'),
      body: t(
        'CIV membership, RIF, mercantile registry, ID card and a clear selfie. The full form takes ~5 minutes.',
        'Membresía CIV, RIF, Registro Mercantil, cédula y un selfie nítido. El formulario completo toma ~5 minutos.',
      ),
    },
    {
      icon: ScanFace,
      title: t('2. AI + human review', '2. Revisión IA + humana'),
      body: t(
        'Our AI extracts data from each document (OCR + cross-checks). Anything ambiguous escalates to a WakaCasa reviewer the same business day.',
        'Nuestra IA extrae datos de cada documento (OCR + verificación cruzada). Lo ambiguo escala a un revisor WakaCasa el mismo día hábil.',
      ),
    },
    {
      icon: Award,
      title: t('3. Get the gold "Verified" badge', '3. Obtén el sello "Verificado" dorado'),
      body: t(
        'Your public profile carries the Verified seal for 12 months. +25 points on your Trust Score, premium placement in search, and trust signals to overseas buyers.',
        'Tu perfil público lleva el sello Verificado por 12 meses. +25 puntos en tu Trust Score, posicionamiento prioritario en búsqueda y señales de confianza para compradores del exterior.',
      ),
    },
  ];

  const bullets = [
    t('Faster lead conversion — buyers contact verified agents first.', 'Mayor conversión — los compradores contactan primero a agentes verificados.'),
    t('Premium placement on every search page and bento map.', 'Posicionamiento prioritario en cada página de búsqueda y mapa bento.'),
    t('Top-Agents leaderboard eligibility (city + national).', 'Elegibilidad para el ranking Top-Agentes (ciudad + nacional).'),
    t('Trust Score boost — visible on your public profile and every listing.', 'Boost en Trust Score — visible en tu perfil público y cada propiedad.'),
    t('Eligible to publish more listings (Free tier 1 → Verified 5+).', 'Habilitado para publicar más propiedades (Free 1 → Verificado 5+).'),
    t('Required for Overseas Buyer transactions and Tour Bookings.', 'Requerido para transacciones de compradores del exterior y agendar tours.'),
  ];

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="why-verified-agents-page">
      {/* HERO */}
      <section className="bg-[#0B192C] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <div className="flex items-center gap-2 mb-5">
            <span className="inline-flex items-center gap-2 bg-[#D4AF37]/10 text-[#D4AF37] px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-[0.18em]">
              <Sparkles className="w-3.5 h-3.5" />
              {t('Why get verified', '¿Por qué verificarte?')}
            </span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.1] mb-5">
            {t('Win more clients with a ', 'Gana más clientes con un ')}
            <span className="text-[#D4AF37]">{t('Verified', 'Verificado')}</span>
            {t(' badge.', ' badge.')}
          </h1>
          <p className="text-base sm:text-lg text-white/70 max-w-2xl leading-relaxed mb-8">
            {t(
              'Buyers in Venezuela — and especially the diaspora — pick the agent they trust most. WakaCasa\'s verification is the fastest way to stand out, rank higher, and unlock the platform\'s most valuable workflows.',
              'Los compradores en Venezuela —y especialmente la diáspora— eligen al agente en quien más confían. La verificación WakaCasa es la forma más rápida de destacar, posicionarte mejor y desbloquear los flujos más valiosos de la plataforma.',
            )}
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/verification">
              <Button className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] font-semibold h-12 px-7 rounded-xl gap-2" data-testid="why-agents-cta-primary">
                {t('Start my verification', 'Empezar mi verificación')}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/auth">
              <Button variant="ghost" className="text-white hover:bg-white/10 h-12 px-6 rounded-xl">
                {t('Create my agent account', 'Crear cuenta de agente')}
              </Button>
            </Link>
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-6 text-xs text-white/50 font-medium">
            <span>⚡ {t('Approval in <24h', 'Aprobación en <24h')}</span>
            <span>🔒 {t('Documents encrypted at rest', 'Documentos cifrados en reposo')}</span>
            <span>💎 {t('Valid 12 months', 'Válido 12 meses')}</span>
          </div>
        </div>
      </section>

      {/* SCREENSHOT + DESCRIPTION */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="uppercase tracking-[0.2em] text-xs font-medium text-[#D4AF37] mb-3">
              {t('The verification dashboard', 'El panel de verificación')}
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl text-[#0B192C] font-semibold mb-4">
              {t('One single flow. 5 documents. Done.', 'Un solo flujo. 5 documentos. Listo.')}
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              {t(
                'Drag-and-drop each document, watch the AI auto-extract your CIV number, RIF and ID details in real time. Replace any document anytime — even after approval.',
                'Arrastra cada documento, mira cómo la IA extrae tu número CIV, RIF y datos de cédula en tiempo real. Reemplaza cualquier documento cuando quieras — incluso después de aprobado.',
              )}
            </p>
            <p className="text-sm text-slate-500 leading-relaxed">
              {t(
                'If anything looks off, our team reviews manually and gets back to you the same business day with a clear next step.',
                'Si algo se ve raro, nuestro equipo revisa manualmente y te responde el mismo día hábil con un siguiente paso claro.',
              )}
            </p>
          </div>
          <div className="relative">
            <div className="absolute -inset-3 bg-gradient-to-br from-[#D4AF37]/20 to-transparent rounded-2xl blur-xl" />
            <img
              src="/sales/agent-verification.jpeg"
              alt={t('WakaCasa verification dashboard', 'Panel de verificación WakaCasa')}
              className="relative rounded-2xl shadow-2xl border border-slate-200 w-full"
              loading="lazy"
              data-testid="why-agents-screenshot"
            />
          </div>
        </div>
      </section>

      {/* SORA INTRO CLIP — 8s branded opener */}
      <section className="bg-[#0B192C] py-14" data-testid="why-agents-intro-video-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <p className="uppercase tracking-[0.2em] text-xs font-medium text-[#D4AF37] mb-3 text-center">
            {t('Brand intro', 'Intro de marca')}
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl text-white font-semibold mb-6 text-center">
            {t('The verified agent standard, in motion', 'El estándar del agente verificado, en movimiento')}
          </h2>
          <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-[#D4AF37]/30 aspect-video">
            <video
              src="/videos/sora_intro.mp4"
              className="w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              controls={false}
              data-testid="why-agents-intro-video"
            />
          </div>
          <p className="text-center text-xs text-[#D4AF37]/70 mt-4">
            {t(
              'Full 8-minute walkthrough coming soon · this is our brand intro.',
              'Tutorial completo de 8 minutos próximamente · esta es nuestra intro de marca.'
            )}
          </p>
        </div>
      </section>

      {/* STEPS */}
      <section className="bg-white border-y border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
          <p className="uppercase tracking-[0.2em] text-xs font-medium text-[#D4AF37] mb-3 text-center">
            {t('3 simple steps', '3 pasos simples')}
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl text-[#0B192C] font-semibold mb-12 text-center">
            {t('How verification works', 'Cómo funciona la verificación')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {steps.map((s, i) => (
              <div key={i} className="bg-slate-50 rounded-2xl p-6 border border-slate-100" data-testid={`why-agents-step-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-[#0B192C] text-[#D4AF37] flex items-center justify-center mb-4">
                  <s.icon className="w-5 h-5" />
                </div>
                <h3 className="font-heading text-base text-[#0B192C] font-semibold mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BULLETS */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
        <p className="uppercase tracking-[0.2em] text-xs font-medium text-[#D4AF37] mb-3">
          {t('What you unlock', 'Qué desbloqueas')}
        </p>
        <h2 className="font-heading text-2xl sm:text-3xl text-[#0B192C] font-semibold mb-8">
          {t('Why agents verify on WakaCasa', '¿Por qué los agentes se verifican en WakaCasa?')}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {bullets.map((b, i) => (
            <div key={i} className="flex items-start gap-3 bg-white border border-slate-100 rounded-xl p-4">
              <CheckCircle2 className="w-5 h-5 text-[#D4AF37] mt-0.5 shrink-0" />
              <span className="text-sm text-slate-700 leading-relaxed">{b}</span>
            </div>
          ))}
        </div>
      </section>

      {/* CTA FOOTER */}
      <section className="bg-[#0B192C] text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center">
          <ShieldCheck className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold mb-4">
            {t('Ready to get verified?', '¿Listo para verificarte?')}
          </h2>
          <p className="text-white/70 leading-relaxed mb-8 max-w-xl mx-auto">
            {t(
              'USD $49 — one-time fee, valid 12 months. Pay with Bank Transfer, Pago Móvil, or Zelle.',
              'USD $49 — pago único, válido 12 meses. Paga con Transferencia Bancaria, Pago Móvil o Zelle.',
            )}
          </p>
          <Link to="/verification">
            <Button className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] font-semibold h-12 px-8 rounded-xl gap-2" data-testid="why-agents-cta-footer">
              {t('Start now', 'Empezar ahora')}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
